import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatCPF } from '@wareclouds/frontend-common';
import { formatRut } from '@wareclouds/frontend-common/b2c';
import { WButton } from '@wareclouds/wareclouds-ui';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';

import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  CreateAccountStepsIndicator,
  OnboardingPageLayout,
} from '../../../shared';
import { useRegistration } from '../../hooks';
import { IS_BR, VEHICLE_TYPES } from '../../utils/registrationConfig';
import { BirthDateField, RegistrationSuccessModal } from '../components';

const TODAY = dayjs();
const LOCALE = IS_BR ? 'pt-br' : 'es';

export default function RegistrationPage() {
  const { t } = useTranslation(['onboarding', 'button']);
  const {
    control,
    handleSubmit,
    formState,
    isSubmitting,
    hasPersonalDetailsStage,
    showSuccessModal,
    handleDismissSuccessModal,
    onSubmit,
    submitError,
  } = useRegistration();
  const { errors } = formState;

  const steps = [
    { label: t('steps.profile') },
    { label: t('steps.training') },
    { label: t('steps.registration') },
  ];

  const vf = (key) => t(`registration.page.fields.vehicle_type.options.${key}`);
  const contentPb = submitError
    ? 'calc(220px + env(safe-area-inset-bottom, 0px))'
    : 'calc(120px + env(safe-area-inset-bottom, 0px))';

  return (
    <OnboardingPageLayout
      contentPb={contentPb}
      footer={
        <Stack spacing={1}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <WButton
            label={t('submit', { ns: 'button' })}
            variant="contained"
            color="primary"
            fullWidth
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            type="submit"
            form="registration-form"
          />
        </Stack>
      }
    >
      <CreateAccountStepsIndicator
        steps={steps}
        activeStep={2}
        completedSteps={[0, 1]}
      />

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '2rem', lineHeight: 1.2 }}>🔍</Typography>
        <Typography variant="h5" fontWeight={500} sx={{ mt: 0.5 }}>
          {t('registration.page.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {t('registration.page.description')}
        </Typography>
      </Box>

      <form id="registration-form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Controller
            name="birth_date"
            control={control}
            render={({ field }) => (
              <BirthDateField
                label={t('registration.page.fields.birth_date.label')}
                placeholder={t(
                  'registration.page.fields.birth_date.placeholder'
                )}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.birth_date}
                helperText={errors.birth_date?.message}
                disabled={hasPersonalDetailsStage || isSubmitting}
                locale={LOCALE}
                maxDate={TODAY}
              />
            )}
          />

          {!IS_BR && (
            <Controller
              name="rut"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('registration.page.fields.rut.label')}
                  placeholder={t('registration.page.fields.rut.placeholder')}
                  variant="outlined"
                  fullWidth
                  error={!!errors.rut}
                  helperText={errors.rut?.message}
                  disabled={hasPersonalDetailsStage || isSubmitting}
                  onChange={(e) => field.onChange(formatRut(e.target.value))}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          )}

          {IS_BR && (
            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('registration.page.fields.cpf.label')}
                  placeholder={t('registration.page.fields.cpf.placeholder')}
                  variant="outlined"
                  fullWidth
                  error={!!errors.cpf}
                  helperText={errors.cpf?.message}
                  disabled={hasPersonalDetailsStage || isSubmitting}
                  onChange={(e) => field.onChange(formatCPF(e.target.value))}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          )}

          {IS_BR && (
            <Controller
              name="neighborhood"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('registration.page.fields.neighborhood.label')}
                  placeholder={t(
                    'registration.page.fields.neighborhood.placeholder'
                  )}
                  variant="outlined"
                  fullWidth
                  error={!!errors.neighborhood}
                  helperText={errors.neighborhood?.message}
                  disabled={hasPersonalDetailsStage || isSubmitting}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          )}

          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('registration.page.fields.address.label')}
                placeholder={t('registration.page.fields.address.placeholder')}
                variant="outlined"
                fullWidth
                error={!!errors.address}
                helperText={errors.address?.message}
                disabled={hasPersonalDetailsStage || isSubmitting}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />

          <Controller
            name="vehicle_type"
            control={control}
            render={({ field }) => (
              <FormControl
                error={!!errors.vehicle_type}
                disabled={isSubmitting}
              >
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  {t('registration.page.fields.vehicle_type.question')}
                </Typography>
                <RadioGroup
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  sx={{ pl: 2 }}
                >
                  {VEHICLE_TYPES.map((typeId) => (
                    <FormControlLabel
                      key={typeId}
                      value={typeId}
                      control={<Radio />}
                      label={vf(typeId)}
                    />
                  ))}
                </RadioGroup>
                {errors.vehicle_type && (
                  <Typography variant="caption" color="error">
                    {errors.vehicle_type.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Stack>
      </form>

      <RegistrationSuccessModal
        open={showSuccessModal}
        onDismiss={handleDismissSuccessModal}
        translationKeyPrefix="registration.page.modal.success"
      />
    </OnboardingPageLayout>
  );
}
